/**
 * The pure helpers behind the three tables of the Similar watersheds tab (client, 7 October
 * 2026): how a similarity, a percentage and a signed gap are printed, and which of four
 * shapes the Most-alike block's look-out sentence takes. Kept apart from the JSX parts in
 * `Uc2TableParts.tsx` so each file exports one kind of thing.
 */
import type { IntelSimilarRow } from "../../lib/api";
import { UC2_ADOPTION_GAP_POINTS } from "./uc2Constants";

/** `similarity` (0 to 1) as whole percent, clamped; an older row without one reads 0. */
export function alikePct(similarity: number | null | undefined): number {
  if (typeof similarity !== "number" || !Number.isFinite(similarity)) return 0;
  return Math.round(Math.min(1, Math.max(0, similarity)) * 100);
}

/** A percentage the way every cell prints it: the number, a narrow no-break space, `%`. */
export function pctText(pct: number): string {
  return `${pct}\u202f%`;
}

/** A signed whole number with a real minus sign, `+12`, `-12` (with U+2212) or `0`. */
export function signedText(value: number): string {
  const n = Math.round(value);
  if (n === 0) return "0";
  return n > 0 ? `+${n}` : `\u2212${Math.abs(n)}`;
}

/** The look-out sentence's four shapes, read off the three rows the table shows. */
export function similarSummary(
  rows: IntelSimilarRow[],
  threshold: number = UC2_ADOPTION_GAP_POINTS,
): { kind: "learning" | "more" | "noValue" | "similar"; names: string[] } {
  const learning = rows.filter((r) => r.learning_opportunity);
  if (learning.length)
    return { kind: "learning", names: learning.map((r) => r.name) };
  // `gap` is reference minus candidate, so a gap at or below minus the threshold is a
  // candidate that reports the practice clearly more often than the focused watershed.
  const more = rows.filter(
    (r) => typeof r.gap === "number" && Math.round(r.gap) <= -threshold,
  );
  if (more.length) return { kind: "more", names: more.map((r) => r.name) };
  if (rows.length && rows.every((r) => typeof r.gap !== "number")) {
    return { kind: "noValue", names: [] };
  }
  return { kind: "similar", names: [] };
}

/**
 * The one sentence an open pair says about its gap, as a locale key and its interpolation.
 *
 * Read off `gap` (a minus b, in points) rather than off the two adoption figures, because
 * the gap is what the engine stored. The points are **rounded before the direction is
 * chosen**: a 0.4-point difference reads "similar", never "reports 0 points less".
 */
export function pairSentence(
  a: string,
  b: string,
  practice: string,
  gap: number | null,
): { key: string; options: Record<string, unknown> } {
  const points = gap == null ? null : Math.round(Math.abs(gap));
  if (points == null)
    return { key: "intel.uc2.pairNoValue", options: { practice } };
  if (points === 0)
    return { key: "intel.uc2.pairSame", options: { a, b, practice } };
  return {
    key: "intel.uc2.pairLess",
    options: {
      count: points,
      low: gap! > 0 ? b : a,
      high: gap! > 0 ? a : b,
      practice,
    },
  };
}
